import { useState } from "react";
import type { Location, ShopifyOrderView } from "../../api/types";
import { whenText } from "./report/webOrdersText";
import { calendarDayLabel, dueDayText, dueDistanceText, orderLineSummary } from "./webOrderDates";

function eur(value: string): string {
  return `€${Number(value).toFixed(2)}`;
}

/** Where it goes: the pickup shop, or the area of the delivery address. */
function deliverTo(order: ShopifyOrderView, locations: Location[]): string {
  if (order.fulfillmentMethod === "PICK_UP") {
    const shop = locations.find((location) => location.id === order.offeredToLocationId)?.name;
    return shop ? `Pickup · ${shop}` : "Pickup";
  }
  const address = order.shippingAddress;
  return [address?.city, address?.zip].filter(Boolean).join(", ") || "—";
}

/**
 * The stock column. Before its preparation day an order's stock is not
 * checked (OD-283), so this says when it will be — and, as a hint, when no
 * shop could make it from today's stock.
 */
function StockPill({ order }: { order: ShopifyOrderView }) {
  const short = order.feasibility && order.feasibility.candidates.length === 0 ? order.feasibility.shortfall : [];
  if (short.length) {
    return <span className="wo-attn is-soon">Short today: {short.map((line) => line.text).join("; ")}</span>;
  }
  return order.due ? <span className="wo-attn is-info">Stock check {calendarDayLabel(order.due.prepDay)}</span> : null;
}

function AssignRow({
  order,
  locations,
  busy,
  onAssign,
}: {
  order: ShopifyOrderView;
  locations: Location[];
  busy: boolean;
  onAssign: (order: ShopifyOrderView, locationId: string) => void;
}) {
  // A pickup goes only to the shop the customer chose (the server checks it too).
  const pickupShop = order.fulfillmentMethod === "PICK_UP" ? order.offeredToLocationId : null;
  const choices = pickupShop ? locations.filter((location) => location.id === pickupShop) : locations;
  const [shopId, setShopId] = useState(pickupShop ?? "");
  const label = order.orderNumber ?? order.shopifyOrderId;
  return (
    <tr>
      <td>
        <strong>{dueDayText(order) ?? "—"}</strong>
        <br />
        <span className="muted">{dueDistanceText(order)}</span>
      </td>
      <td>
        {label}
        <br />
        <span className="muted">arrived {whenText(order.receivedAt)}</span>
      </td>
      <td>{orderLineSummary(order)}</td>
      <td>
        {deliverTo(order, locations)}
        {order.due?.window ? (
          <>
            <br />
            <span className="muted">{order.due.window}</span>
          </>
        ) : null}
      </td>
      <td className="wo-assign-money">{eur(order.totalEur)}</td>
      <td>
        <StockPill order={order} />
      </td>
      <td>
        <select aria-label={`Shop for ${label}`} value={shopId} onChange={(e) => setShopId(e.target.value)}>
          {pickupShop ? null : <option value="">Choose a shop</option>}
          {choices.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <button type="button" className="button" disabled={busy || !shopId} onClick={() => onAssign(order, shopId)}>
          Assign
        </button>
      </td>
    </tr>
  );
}

/**
 * G-167 (OD-283, mockup approved 2026-10-22 as OD-288) — the office's
 * "To assign" list: web orders due later than tomorrow, which no tablet sees
 * until the owner or an admin picks the shop; below it, the orders already
 * assigned and waiting for their preparation day, each with "Pass to
 * another shop".
 */
export function WebOrdersToAssign({
  orders,
  locations,
  busy,
  error,
  onAssign,
  onPass,
}: {
  orders: ShopifyOrderView[];
  locations: Location[];
  busy: boolean;
  error: string | null;
  onAssign: (order: ShopifyOrderView, locationId: string) => void;
  onPass: (order: ShopifyOrderView) => void;
}) {
  const byDay = (a: ShopifyOrderView, b: ShopifyOrderView) => (a.due?.day ?? "").localeCompare(b.due?.day ?? "");
  const toAssign = orders.filter((order) => order.toAssign).sort(byDay);
  const waiting = orders
    .filter((order) => order.status === "PREPARING" && order.due?.when === "LATER" && (order.assigned || order.lastPass))
    .sort(byDay);
  return (
    <div className="wo-assign">
      {error ? <div className="alert error">{error}</div> : null}
      <div className="card table-scroll">
        <table aria-label="Web orders to assign">
          <thead>
            <tr>
              <th>For</th>
              <th>Order</th>
              <th>What</th>
              <th>Deliver to</th>
              <th>Total</th>
              <th>Stock on the prep day</th>
              <th>Shop</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {toAssign.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted">
                  Nothing to assign.
                </td>
              </tr>
            ) : null}
            {toAssign.map((order) => (
              <AssignRow key={order.id} order={order} locations={locations} busy={busy} onAssign={onAssign} />
            ))}
          </tbody>
        </table>
      </div>

      {waiting.length ? (
        <section className="card wo-assign-waiting" aria-label="Assigned, waiting for their prep day">
          <h3>Assigned · waiting for their prep day</h3>
          {waiting.map((order) => (
            <div key={order.id} className="wo-assign-waiting-row">
              <div>
                <strong>{dueDayText(order)}</strong> · {order.orderNumber ?? order.shopifyOrderId} · {orderLineSummary(order)} ·{" "}
                {eur(order.totalEur)}
              </div>
              <div className="wo-assign-waiting-meta">
                <span className="badge primary">{order.claimedLocation?.name ?? "—"}</span>
                <span className="muted">
                  {order.lastPass
                    ? `passed by ${order.lastPass.by.fullName} · ${whenText(order.lastPass.at)}`
                    : `assigned by ${order.assigned?.by?.fullName ?? "the office"} · ${whenText(order.assigned?.at ?? order.receivedAt)}`}
                </span>
                {order.fulfillmentMethod === "PICK_UP" ? null : (
                  <button type="button" className="button plain" disabled={busy} onClick={() => onPass(order)}>
                    Pass to another shop
                  </button>
                )}
              </div>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
